/* eslint-disable jsx-a11y/anchor-is-valid */
/**
 * Self-service password reset flow: email -> OTP -> new password -> success.
 * Renders inside AuthLayout and reuses the login form styling + the shared
 * OtpScreen (also used by MFA). Text comes from the app-wide react-intl
 * catalogue (AUTH.NATIVE.* keys).
 */
import {FormEvent, useState} from 'react';
import {useIntl} from 'react-intl';

import AuthLayout from './AuthLayout';
import OtpScreen from './OtpScreen';
import PasswordField from './PasswordField';
import PasswordRules from './PasswordRules';
import {generatePassword, isPasswordValid} from './passwordPolicy';
import {EnvelopeIcon, LockIcon, WandIcon} from './nativeIcons';
import {PASSWORD_MISMATCH_KEY} from './errorMessages';
import {useResetPassword} from './useResetPassword';

interface ResetPasswordProps {
  onClose: () => void;
  /** Pre-fill the email (e.g. carried over from the login form). */
  initialEmail?: string;
  /** Optional banner shown on the email step (e.g. migration reset required). */
  notice?: string;
  /** Override the email-step title (defaults to the "forgot password" title). */
  title?: string;
}

const ResetPassword = ({onClose, initialEmail, notice, title}: ResetPasswordProps): JSX.Element => {
  const intl = useIntl();
  const f = (id: string): string => intl.formatMessage({id});
  const {
    status,
    error,
    notice: resentNotice,
    challenge,
    start,
    verifyCode,
    resendCode,
    submitPassword,
  } = useResetPassword();
  const [email, setEmail] = useState(initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showMismatch, setShowMismatch] = useState(false);
  const [revealNonce, setRevealNonce] = useState(0);

  const sending = status === 'sendingCode';
  const submitting = status === 'submitting';

  // Live confirmation state — drives the confirm field border as the user types.
  const confirmState: 'none' | 'match' | 'mismatch' =
    confirm.length === 0 ? 'none' : confirm === password ? 'match' : 'mismatch';
  const confirmClass =
    confirmState === 'mismatch'
      ? 'input-error'
      : confirmState === 'match'
      ? 'input-valid'
      : undefined;

  const handleEmailSubmit = (event: FormEvent): void => {
    event.preventDefault();
    start(email.trim());
  };

  const handlePasswordSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!isPasswordValid(password, email)) {
      return;
    }
    if (confirmState === 'mismatch') {
      setShowMismatch(true);
      return;
    }
    setShowMismatch(false);
    submitPassword(password);
  };

  const handleGenerate = (): void => {
    const generated = generatePassword(email);
    setPassword(generated);
    setConfirm(generated);
    setShowMismatch(false);
    setRevealNonce((nonce) => nonce + 1);
  };

  const mismatchError = showMismatch ? f(PASSWORD_MISMATCH_KEY) : undefined;

  const backLink = (
    <a
      href="#"
      className="native-back"
      onClick={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      ← {f('AUTH.NATIVE.BACK_TO_LOGIN')}
    </a>
  );

  if (status === 'completed') {
    return (
      <AuthLayout>
        <div className="native-form">
          <h2 className="native-title">{f('AUTH.NATIVE.RESET.SUCCESS_TITLE')}</h2>
          <p className="native-subtitle">{f('AUTH.NATIVE.RESET.SUCCESS_BODY')}</p>
          <button type="button" className="native-submit" onClick={onClose}>
            {f('AUTH.NATIVE.BACK_TO_LOGIN')}
          </button>
        </div>
      </AuthLayout>
    );
  }

  if (status === 'passwordRequired' || status === 'submitting') {
    return (
      <AuthLayout>
        {backLink}
        <form className="native-form" onSubmit={handlePasswordSubmit}>
          <h2 className="native-title">{f('AUTH.NATIVE.RESET.NEW_PASSWORD_TITLE')}</h2>

          {mismatchError || error ? (
            <div className="error pageLevel" role="alert">
              <p className="mb-0">{mismatchError || error}</p>
            </div>
          ) : null}

          <div className="native-field">
            <label className="native-label" htmlFor="reset-password">
              {f('AUTH.NATIVE.FIELD.NEW_PASSWORD')}
            </label>
            <PasswordField
              id="reset-password"
              leftIcon={<LockIcon />}
              revealNonce={revealNonce}
              placeholder={f('AUTH.NATIVE.FIELD.NEW_PASSWORD')}
              aria-label={f('AUTH.NATIVE.FIELD.NEW_PASSWORD')}
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                if (showMismatch) {
                  setShowMismatch(false);
                }
              }}
              autoFocus
              required
            />
            <button type="button" className="native-generate-btn" onClick={handleGenerate}>
              <WandIcon />
              {f('AUTH.NATIVE.PASSWORD.GENERATE')}
            </button>
          </div>

          <div className="native-field">
            <label className="native-label" htmlFor="reset-confirm">
              {f('AUTH.NATIVE.FIELD.CONFIRM_PASSWORD')}
            </label>
            <PasswordField
              id="reset-confirm"
              leftIcon={<LockIcon />}
              className={confirmClass}
              placeholder={f('AUTH.NATIVE.FIELD.CONFIRM_PASSWORD')}
              aria-label={f('AUTH.NATIVE.FIELD.CONFIRM_PASSWORD')}
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => {
                setConfirm(event.target.value);
                if (showMismatch) {
                  setShowMismatch(false);
                }
              }}
              required
            />
          </div>

          <PasswordRules password={password} email={email} />

          <button
            type="submit"
            className="native-submit"
            disabled={submitting || !isPasswordValid(password, email)}
          >
            {submitting ? f('AUTH.NATIVE.RESET.SUBMITTING') : f('AUTH.NATIVE.RESET.SUBMIT')}
          </button>
        </form>
      </AuthLayout>
    );
  }

  if (status === 'codeSent' || status === 'verifying') {
    // Neutral, anti-enumeration message: identical whether or not the account
    // exists (the masked address is derived from what the user typed).
    const ssprInfo = intl.formatMessage(
      {id: 'AUTH.NATIVE.SSPR.CODE_SENT'},
      {email: challenge?.sentTo || f('AUTH.NATIVE.OTP.YOUR_EMAIL')}
    );

    return (
      <AuthLayout>
        {backLink}
        <OtpScreen
          title={f('AUTH.NATIVE.VERIFICATION')}
          sentTo={challenge?.sentTo}
          infoMessage={resentNotice ?? ssprInfo}
          verifying={status === 'verifying'}
          error={error}
          onSubmit={verifyCode}
          onResend={resendCode}
        />
      </AuthLayout>
    );
  }

  // idle / sendingCode / failed -> email entry
  return (
    <AuthLayout>
      {backLink}
      <form className="native-form" onSubmit={handleEmailSubmit}>
        <h2 className="native-title">{title ?? f('AUTH.NATIVE.RESET.FORGOT_TITLE')}</h2>

        {notice ? (
          <div className="info pageLevel" role="status">
            <p className="mb-0">{notice}</p>
          </div>
        ) : null}

        {error ? (
          <div className="error pageLevel" role="alert">
            <p className="mb-0">{error}</p>
          </div>
        ) : null}

        <div className="native-field">
          <label className="native-label" htmlFor="reset-email">
            {f('AUTH.NATIVE.FIELD.EMAIL')}
          </label>
          <div className="native-input">
            <span className="field-left-icon">
              <EnvelopeIcon />
            </span>
            <input
              type="email"
              id="reset-email"
              placeholder={f('AUTH.NATIVE.FIELD.EMAIL')}
              aria-label={f('AUTH.NATIVE.FIELD.EMAIL')}
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoFocus
              required
            />
          </div>
        </div>

        <button type="submit" className="native-submit" disabled={sending}>
          {sending ? f('AUTH.NATIVE.RESET.SENDING') : f('AUTH.NATIVE.RESET.SEND_CODE')}
        </button>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
